import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Server, Activity, CheckCircle2, TrendingUp, Shield, Clock } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Azure Machine Learning', icon: null, image: '/Azure_machine_learning.jpg' },
  { name: 'Python/Pandas', icon: null, image: '/Python.jpg' },
  { name: 'TensorFlow', icon: null, image: '/Tensorflow.png' },
  { name: 'Apache Kafka', icon: Database, image: null },
  { name: 'Redis Cache', icon: Server, image: null },
  { name: 'FHIR HL7', icon: Activity, image: null },
  { name: 'Azure Functions', icon: null, image: '/Azure_Functions.png' }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '99.99%',
    label: 'System Uptime',
    description: 'Fault-tolerant architecture with real-time failover'
  },
  {
    icon: Shield,
    value: '78%',
    label: 'Reduction in Transfer Incidents',
    description: 'Through comprehensive situational awareness'
  },
  {
    icon: Clock,
    value: '2 min',
    label: 'Critical Response Time',
    description: 'Down from hours to under 2 minutes'
  },
  {
    icon: CheckCircle2,
    value: '94%',
    label: 'Medical Terminology Accuracy',
    description: 'Real-time processing of clinical notes'
  }
];

const highlights = [
  {
    category: 'System Performance',
    items: [
      'Achieved 99.99% system uptime with fault-tolerant architecture and real-time failover mechanisms',
      'Processed millions of clinical data points daily while maintaining HIPAA compliance',
      'Cut critical information response time from hours to under 2 minutes'
    ]
  },
  {
    category: 'Patient Safety Impact',
    items: [
      'Eliminated communication gaps that were causing medication errors and delayed treatments',
      'Reduced patient transfer incidents by 78% through comprehensive situational awareness',
      'Integrated 40+ different EMR systems into unified clinical intelligence platform'
    ]
  },
  {
    category: 'AI & Data Processing',
    items: [
      'Achieved real-time processing of unstructured clinical notes with 94% accuracy in medical terminology extraction'
    ]
  }
];

const challenges = [
  {
    title: 'Fragmented EMR Systems',
    icon: Database,
    problem: 'Dozens of hospitals each using different EMR systems with incompatible data formats and communication protocols, creating information silos during critical patient care moments.',
    solution: 'Built a distributed data integration platform using Apache Kafka and FHIR HL7 standards to create real-time data streams from all EMR systems. Implemented universal data transformation pipelines that could normalize clinical data regardless of source system format.',
    impact: 'Unified 40+ EMR systems into single platform'
  },
  {
    title: 'Medical Language Processing',
    icon: Activity,
    problem: 'Clinical notes contained complex medical terminology, abbreviations, and specialty-specific language that varied across departments, making automated information extraction extremely challenging.',
    solution: 'Developed specialized NLP models using TensorFlow that could understand medical context, terminology, and abbreviations. The system achieved 94% accuracy in extracting critical patient information from unstructured clinical documentation.',
    impact: '94% accuracy in medical terminology extraction'
  },
  {
    title: 'Patient Safety & Reliability',
    icon: Shield,
    problem: 'Healthcare requires 24/7 availability with zero tolerance for system failures, as missing information during emergencies could result in life-threatening situations.',
    solution: 'Engineered fault-tolerant architectures with distributed processing across multiple Azure regions, real-time failover mechanisms, and comprehensive audit trails. Implemented Redis caching for instant data retrieval and redundant data storage systems.',
    impact: '99.99% system uptime achieved'
  },
  {
    title: 'Regulatory Compliance',
    icon: Server,
    problem: 'Processing sensitive patient data across multiple facilities while maintaining strict HIPAA compliance, audit trails, and data governance requirements.',
    solution: 'Built enterprise-grade security protocols with end-to-end encryption, role-based access controls, and automated compliance monitoring. All data processing maintains comprehensive audit trails for regulatory reporting.',
    impact: 'Full HIPAA compliance maintained'
  }
];

const HealthcareCommunication = () => {
  return (
    <>
      <SEOHead
        title="Healthcare Communication Case Study | Agility"
        description="AI-powered healthcare communication platform improving patient outcomes."
        url="https://www.agilitytech.ai/case-studies/healthcare-communication"
      />
    <div className="pt-20 bg-black">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80"
            alt="Healthcare Communication Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Healthcare Technology</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Real-Time Clinical Intelligence Platform
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Eliminating communication breakdowns across multi-facility healthcare networks through 
              AI-powered clinical data integration and real-time patient safety monitoring.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
              <p className="text-white">Healthcare/Hospital Networks</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
              <p className="text-white">North America</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
              <p className="text-white">Multi-Facility Healthcare Network</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
              <p className="text-white">Q1 2024 - Q3 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                {tech.image ? (
                  <img 
                    src={tech.image} 
                    alt={tech.name}
                    className="h-6 w-6 object-contain"
                  />
                ) : (
                  <tech.icon className="h-6 w-6 text-teal-400" />
                )}
                <span className="text-gray-300 font-medium">{tech.name}</span>
              </div>
            ))}
            <div className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
              <img 
                src="/HIPAA_Complaint.jpg" 
                alt="HIPAA Compliant"
                className="h-6 w-6 object-contain"
              />
              <span className="text-gray-300 font-medium">HIPAA Compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <Shield className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Critical Communication Breakdowns</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    A multi-facility healthcare network spanning dozens of hospitals struggled with critical 
                    patient safety incidents caused by communication breakdowns during patient transfers and 
                    emergencies. Each facility operated different EMR systems with inconsistent communication 
                    protocols, making it nearly impossible to maintain comprehensive patient awareness across 
                    the network. Clinical teams were missing critical patient updates, leading to medication 
                    errors, delayed treatments, and compromised patient safety outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Unified Clinical Intelligence Platform</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    We replaced fragmented, error-prone communication systems with an intelligent, unified 
                    platform that transformed patient safety across the entire healthcare network. By 
                    implementing cutting-edge AI and real-time data integration with enterprise-grade 
                    security and HIPAA compliance, we delivered breakthrough clinical outcomes that 
                    exceeded all stakeholder expectations.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {highlights.map((category, index) => (
                <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                  <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                  <div className="space-y-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                        <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <challenge.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                      Problem
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      Solution
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                  </div>
                  
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                    <p className="text-teal-300 font-medium">{challenge.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Activity className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Healthcare Communication?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact our healthcare technology specialists to discover how real-time clinical 
              intelligence can enhance patient safety and operational efficiency across your network.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default HealthcareCommunication;